import React, { type Ref, type CSSProperties, forwardRef, useEffect } from 'react';
import { AiEnabledMini, AiEnabledMiniBody } from '@atlassian/jira-minimizable-modal';
import { AiEnabledMiniHeader } from '../../../../common/ui/ai-enabled';
import Footer, { GIC_VARIANTS } from '../../../../common/ui/footer';
import { useMinimizableModalWrapperProps } from '../../../../controllers/minimizable-modal-wrapper-props';
import type { MinimizableAiEnabledProps } from './types';

export const MinimizableAiEnabled = forwardRef<HTMLDivElement, MinimizableAiEnabledProps>(
	({ children, ...rest }: MinimizableAiEnabledProps, ref: Ref<HTMLDivElement> | undefined) => {
		const {
			onModalCloseWithConfirm,
			onSubmit,
			isSubmitting,
			isOperationInProgress,
			canCreateAnotherIssue,
			onCreateAnotherChange,
			getShouldDisableSubmit,
			createdIssues,
			displayCreateAnotherIssueToggle,
			setShouldRestore,
		} = useMinimizableModalWrapperProps();

		/**
		 * Never restore issue create for ai-enabled as we should not save data that comes from AI.
		 * This should also prevent a reopen when a page is refreshed with an ai-enabled view.
		 */
		useEffect(() => {
			if (setShouldRestore) {
				setShouldRestore(false);
			}
		}, [setShouldRestore]);

		return (
			<AiEnabledMini ref={ref} {...rest}>
				<AiEnabledMiniHeader />
				<AiEnabledMiniBody>{children}</AiEnabledMiniBody>
				<Footer
					onClose={onModalCloseWithConfirm('cancel')}
					onSubmit={onSubmit}
					isSubmitting={isSubmitting}
					isOperationInProgress={isOperationInProgress}
					canCreateAnotherIssue={canCreateAnotherIssue}
					onCreateAnotherChange={onCreateAnotherChange}
					shouldDisableSubmit={getShouldDisableSubmit()}
					createdIssues={createdIssues}
					variant={GIC_VARIANTS.MINI}
					displayCreateAnotherIssueToggle={displayCreateAnotherIssueToggle}
					showCancelButton={false}
					containerStyle={containerStyle}
					showDiscardButton
				/>
			</AiEnabledMini>
		);
	},
);

const containerStyle: CSSProperties = {
	justifyContent: 'flex-end',
};
